/**
 * SliderCustomLabel View Component
 *
 * Pure presentation component that renders custom labels for slider markers.
 * @remarks
 * - Displays one or two marker values as labels
 * - Left label is always shown
 * - Right label is conditionally shown if twoMarkerValue is provided
 *
 * ## Each label is CENTERED UNDER ITS THUMB, not pinned to the track's ends
 *
 * The labels used to be anchored `left: 0` / `right: 0`, i.e. flush with the
 * two ends of the track no matter where the thumbs actually were. That reads as
 * correct only when both thumbs sit at their extremes — and even there it is
 * off, because a thumb centred on the track's end overhangs it by half a ball,
 * so the value sat visibly inboard of the ball it labelled. Drag either handle
 * inwards and the number stayed behind at the end, labelling nothing.
 *
 * `@ptomasroos/react-native-multi-slider` hands the label component the two
 * thumb offsets (`oneMarkerLeftPosition` / `twoMarkerLeftPosition`), measured
 * from the same left origin this component is positioned in — the library's own
 * `DefaultLabel` centres on them the same way. The offset IS the thumb's
 * centre: the marker sits in a 48px container placed at `position - 24` with
 * `alignItems: "center"`.
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { ifWeb } from "@/utils/platform";

const labelHeight = 30;
const topOffset = 24;
/**
 * Fixed box the value is centred inside, so how long the value happens to be
 * cannot shift where the label points. Wide enough for the longest value these
 * sliders show (a short-formatted currency such as `€120M`).
 */
const labelWidth = 56;

/* design-system: dynamic-style waiver — data-driven value (measured slider label position; absolute positioning from slider layout) */
const labelPositionStyle = (markerLeftPosition: number) =>
  ({
    left: markerLeftPosition - labelWidth / 2,
    top: topOffset,
    width: labelWidth,
    height: labelHeight,
  }) as const;

/**
 * Props for SliderCustomLabel view
 */
interface Props {
  oneMarkerValue: number | string;
  twoMarkerValue?: number | string;
  /** Left offset of the first thumb's CENTRE, in px along the track. */
  oneMarkerLeftPosition?: number;
  /** Left offset of the second thumb's CENTRE, in px along the track. */
  twoMarkerLeftPosition?: number;
  isDark: boolean;
}

/**
 * Renders an individual label centred on the thumb it describes
 * @param markerLeftPosition - Left offset of the thumb's centre, in px
 * @param value - The label text value
 * @param isDark - Dark mode indicator
 */
function renderLabel(
  markerLeftPosition: number,
  value: string,
  isDark: boolean
): React.ReactElement {
  return (
    // design-system: dynamic-style waiver — measured slider label position
    <Box
      className="absolute justify-center"
      // eslint-disable-next-line no-restricted-syntax -- continuous slider thumb position drives label offset; not expressible as a closed token
      UNSAFE_style={labelPositionStyle(markerLeftPosition)}
    >
      {/* SE-6773: the web arm used to name an `md` size step, for which this
          theme has no fontSize token — so it compiled to nothing and the
          element rendered at the Text atom's own default (size "md" ->
          text-title-sm). Naming that default keeps the render identical. */}
      <Text
        variant="unstyled"
        className={`text-center font-body ${ifWeb("text-title-sm", "text-title-lg")} ${isDark ? "text-white" : "text-content-primary"}`}
      >
        {value}
      </Text>
    </Box>
  );
}

/**
 * SliderCustomLabel view component
 * @param props - Component props
 * @param props.oneMarkerValue - Value for the first (left) marker
 * @param props.twoMarkerValue - Optional value for the second (right) marker
 * @param props.oneMarkerLeftPosition - Left offset of the first thumb's centre
 * @param props.twoMarkerLeftPosition - Left offset of the second thumb's centre
 * @param props.isDark - Dark mode indicator
 */
const SliderCustomLabelView = memo<Props>(
  ({
    oneMarkerValue,
    twoMarkerValue,
    oneMarkerLeftPosition,
    twoMarkerLeftPosition,
    isDark,
  }) => (
    <Box className="relative">
      {/* A label with no measured thumb offset is not drawn at a guessed
          position — the slider has not laid out yet, and a first frame with the
          value parked at the wrong end is the very artefact this fixes. */}
      {oneMarkerLeftPosition === undefined
        ? null
        : renderLabel(oneMarkerLeftPosition, oneMarkerValue.toString(), isDark)}
      {twoMarkerValue === undefined || twoMarkerLeftPosition === undefined
        ? null
        : renderLabel(twoMarkerLeftPosition, twoMarkerValue.toString(), isDark)}
    </Box>
  )
);

SliderCustomLabelView.displayName = "SliderCustomLabelView";

export default memo(SliderCustomLabelView);
